import { useMemo, useEffect } from 'react';
import { useNearViewport } from '@shared/hooks/useNearViewport';
import { useAccommodationRedirectUrl, createUtmParametersForAccommodationsConsolePage } from '@apps/stays/AccommodationsConsole/hooks/useAccommodationRedirectUrl';
import { useAccommodationsListingMobileLayout } from '@apps/stays/AccommodationsConsole/hooks/useAccommodationsListingMobileLayout';
import { useListingsPanelTelemetry } from '@apps/stays/AccommodationsConsole/components/ListingsPanel/ListingsPanel.telemetry';
import { useSafeUrlParser } from '@shared/hooks/useSafeUrlParser';
import { useMergedUtmParameters } from '@shared/hooks/useMergedUtmParameters';
import { getSelectedRedirectTelemetryName } from '@shared/utils/extractHotelRedirects';
import { getValidCoordinates } from '@shared/core/places';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { withWindow } from '@shared/utils/withWindow';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { HotelSearchSource } from '@shared/constants/accommodations';
import type { ScenarioListingEntryProps, ListingEntryBase } from './ListingEntry.types';

// Shared hook for all scenario listing entries.
// Computes redirect URL, telemetry handlers, point-of-interest, sold-out state,
// and mobile layout — everything that is identical across Guest/Admin/OTA.
export const useListingEntryBase = ({ listing, state, noOfNights, sourceName }: ScenarioListingEntryProps): ListingEntryBase => {
  const { variant, shouldUseNewMobileLayout, isMobileOrTablet } = useAccommodationsListingMobileLayout();
  const isDesktopLayout = !isMobileOrTablet;
  const getRedirectUrl = useAccommodationRedirectUrl(state, sourceName);

  const isSoldOut = listing.type === 'standardHotel' && !!listing.isSoldOutForSelectedDates;

  // Treatment lets a sold-out tile open the PDP. Scoped to the guest/admin search view so only those tiles are bucketed.
  const isSoldOutPdpScopedSource = sourceName === HotelSearchSource.GUEST_MAP || sourceName === HotelSearchSource.ADMIN_MAP;
  const [isSoldOutPdpEnabled] = useIsFeatureEnabled('accommodationsSoldOutPdpExperiment', { skip: !isSoldOut || !isSoldOutPdpScopedSource });
  const isLinkEnabled = !isSoldOut || isSoldOutPdpEnabled;

  // Treatment routes a sold-out tile to the hotel PDP by skipping the interceptor on the redirect page.
  const redirectUrl = getRedirectUrl(listing, { skipAvailabilityCheck: isSoldOut && isSoldOutPdpEnabled });
  const telemetry = useListingsPanelTelemetry();
  const { tq } = useTranslation('accommodationsConsole');
  const parseUrl = useSafeUrlParser();
  const mergedUtm = useMergedUtmParameters(createUtmParametersForAccommodationsConsolePage(sourceName));

  const selectedRedirectTelemetryName = useMemo(() => getSelectedRedirectTelemetryName(listing.redirectMechanisms), [listing.redirectMechanisms]);

  const { ref: containerRef, hasBeenNear } = useNearViewport({ preloadPixels: 0 });

  useEffect(() => {
    if (hasBeenNear && listing.isSponsored) {
      telemetry.promotedHotelImpression({ joyPlaceId: listing.joyPlaceId || '', hotelName: listing.name, chain: listing.chain });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBeenNear]);

  const handleSeeAvailability = useEventCallback(() => {
    telemetry.seeAvailabilityClicked({ joyPlaceId: listing.joyPlaceId || '', hotelName: listing.name, isSponsored: listing.isSponsored ?? false, chain: listing.chain });

    if (redirectUrl) {
      const urlInfo = parseUrl(redirectUrl, 'ListingEntry.handleSeeAvailability');
      if (urlInfo) {
        telemetry.hotelBookingRedirect({
          joyPlaceId: listing.joyPlaceId || '',
          hotelName: listing.name,
          destinationURL: redirectUrl,
          destinationDomain: urlInfo.hostname,
          destinationName: selectedRedirectTelemetryName,
          isSponsored: listing.isSponsored ?? false,
          chain: listing.chain,
          ...mergedUtm,
        });
      }
    }
  });

  const handlePhotoClick = useEventCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const isCarouselControl = target.closest('button, [role="button"], [data-carousel-control]');
    if (!isCarouselControl && isLinkEnabled && redirectUrl) {
      handleSeeAvailability();
      withWindow(global => global.open(redirectUrl, '_blank'));
    }
  });

  const pointOfInterest = useMemo(() => {
    const location = getValidCoordinates(state.circularRegionSearch);
    if (!location) return null;

    let displayName = state.searchLocation || tq`venue`;
    if (state.searchLocation === state.venue.name) displayName = tq`venue`;

    return { displayName, location };
  }, [state.circularRegionSearch, state.searchLocation, state.venue.name, tq]);

  return {
    listing,
    state,
    noOfNights,
    sourceName,
    redirectUrl,
    isSoldOut,
    isLinkEnabled,
    isMobileOrTablet,
    isDesktopLayout,
    shouldUseNewMobileLayout,
    variant,
    pointOfInterest,
    handleSeeAvailability,
    handlePhotoClick,
    containerRef,
  };
};
